import { defineRelease } from '../types'

const ORG_ADMINS = ['iam_org_admin', 'iam_superadmin']

export default defineRelease({
  version: '2026.10.2',
  date: '2026-10-04',
  summary: 'Multiple client portal users per client, clearer scheduled reports, and several fixes.',
  entries: [
    {
      id: 'helpdesk-contextual-configuration-links',
      type: 'new',
      module: 'helpdesk',
      title: 'Open related Helpdesk settings where you need them',
      body: 'Use contextual links beside ticket selections and in configuration sections to manage related priorities, groups, SLA policies, escalation rules and email templates without searching through settings. Return from configuration to continue unfinished ticket forms and related Helpdesk configuration forms with their entered values preserved.',
      link: { to: '/tickets/settings', label: 'Open Ticket Settings' },
      roles: ['iam_superadmin', 'ticketing_admin'],
      service: 'ticket-mgmt',
    },
    {
      id: 'kb-client-document-access',
      type: 'new',
      module: 'helpdesk',
      title: 'Choose which clients can access knowledge base documents',
      body: 'Share published documents with all clients in your organization or select specific clients from a searchable dropdown to grant access. Clients can only find and open documents shared with them, and removing access prevents further viewing.',
      link: { to: '/kb', label: 'Open Knowledge Base' },
      roles: ['iam_superadmin', 'lead_agent'],
      service: 'ticket-mgmt',
    },
    {
      id: 'create-user-department-navigation',
      type: 'new',
      module: 'admin',
      title: 'Open Department Management while creating a user',
      body: 'Use Manage departments next to the Department selection to create a missing department, then return to your unfinished user form without losing the details you entered. The Department selection refreshes when you return so you can use the new department immediately.',
      link: { to: '/admin/organization?tab=users', label: 'Open Users' },
      roles: ORG_ADMINS,
    },
    {
      id: 'org-hub-user-organization-filter',
      type: 'new',
      module: 'platform',
      title: 'Find users by organization in Organization Hub',
      body: 'Filter by organization or choose All Organizations to see everyone in your own organization and only Organization Admin users from other organizations. Combine the filter with username or email search within this existing visibility scope.',
      link: { to: '/admin/organization?tab=users', label: 'Open Users' },
      roles: ['iam_superadmin'],
    },
    {
      id: 'ticket-title-edit',
      type: 'fixed',
      module: 'helpdesk',
      title: 'Edit a ticket’s title from its details page',
      body: 'Use Edit next to the ticket title to correct or update it, then save or cancel your changes. Saved titles appear immediately on the details page and in ticket listings, with existing edit permissions and Description editing unchanged.',
      link: { to: '/tickets', label: 'Open Tickets' },
      roles: ['iam_superadmin', 'ticketing_admin', 'lead_agent', 'ticketing_agent'],
      service: 'ticket-mgmt',
    },
    {
      id: 'client-portal-users',
      type: 'new',
      module: 'helpdesk',
      title: 'Give more people at a client portal access',
      body: 'Each client page now has a Portal users section. Org admins and ticketing admins can add as many people as you need, choose whether they see only their own requests or all of the client’s, and reset passwords, deactivate or remove them.',
      link: { to: '/clients', label: 'Open Clients' },
      roles: [...ORG_ADMINS, 'ticketing_admin', 'lead_agent', 'ticketing_agent'],
      service: 'client-mgmt',
    },
    {
      id: 'org-hub-staff-only',
      type: 'improved',
      module: 'admin',
      title: 'Organization Hub is now just for your team',
      body: 'Client portal roles are no longer offered when creating or editing users. Client portal users are marked in the list and open their client’s page for changes.',
      link: { to: '/admin/organization', label: 'Open Organization Hub' },
      roles: ORG_ADMINS,
    },
    {
      id: 'scheduled-report-preview',
      type: 'improved',
      module: 'helpdesk',
      title: 'See what a scheduled report contains before you send it',
      body: 'Each report section now explains what it shows, and a live PDF preview with sample data updates as you pick sections. Recipients are checked as you type, and the schedule shows exactly which period the report covers.',
      link: { to: '/tickets/settings?section=scheduled-reports', label: 'Open Scheduled Reports' },
      roles: [...ORG_ADMINS, 'ticketing_admin'],
      service: 'ticket-mgmt',
    },
    {
      id: 'scheduled-report-period-fix',
      type: 'fixed',
      module: 'helpdesk',
      title: 'Scheduled reports cover the full previous period',
      body: 'Reports covered only the few moments since the scheduled send time, so they arrived almost empty. A weekly report now covers the whole previous week, and downloads from run history open as PDFs.',
      roles: [...ORG_ADMINS, 'ticketing_admin'],
      service: 'ticket-mgmt',
    },
    {
      id: 'no-reset-on-session-renewal',
      type: 'fixed',
      module: 'general',
      title: 'Pages no longer reset while you work',
      body: 'Every few minutes, when your sign-in renewed in the background, the page could reload itself and clear unsaved form input, filters and open dialogs. Your work now stays put.',
    },
    {
      id: 'sidebar-single-highlight',
      type: 'fixed',
      module: 'general',
      title: 'The sidebar highlights only the page you’re on',
      body: 'Opening a page like Ticket Settings, Asset Analytics or Invoice Settings no longer also highlights Tickets, Asset List or Invoices.',
    },
  ],
})
